import type { APISharedProfileResponse } from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'
import { describeChange, formatSchedule } from './format'
import { labelForSlot } from './labels'
import { Chevron } from '../../components/Chevron'
import './sections.css'

/** Current routine: each product with its cadence, since, ingredients, and the
 * changes logged against it. Measurements only; no efficacy claims. */
export function RoutineSection({
  profile,
}: {
  profile: APISharedProfileResponse
}) {
  if (profile.routine.length === 0) return null
  return (
    <section className='analysis'>
      <h2 className='sec-h'>Routine</h2>
      <p className='sec-sub'>
        The products in use now, with the changes logged against each. Tap a
        product for its ingredients and history.
      </p>
      <div className='card pad'>
        <div className='rlist'>
          {profile.routine.map((item, idx) => {
            const { freq, when } = formatSchedule(item.schedule, item.asNeeded)
            const subtitle = [labelForSlot(item.category), when]
              .filter(Boolean)
              .join(' · ')
            return (
              <details className='ritem' key={`${item.productName}-${idx}`}>
                <summary className='rrow'>
                  <span className='rrow__info'>
                    <span className='rrow__pn'>{item.productName}</span>
                    {subtitle && <span className='rrow__pc'>{subtitle}</span>}
                  </span>
                  <span className='rrow__meta'>
                    <span className='rrow__freq'>{freq}</span>
                    <span className='rrow__since'>
                      since {sDayJs(item.since).format('MMM D, YYYY')}
                    </span>
                  </span>
                  <Chevron />
                </summary>
                <div className='rdetail'>
                  {item.events.length > 0 && (
                    <div className='rdetail__block'>
                      <div className='dl'>Change history</div>
                      <ul className='timeline'>
                        {[...item.events]
                          .sort(
                            (a, b) =>
                              sDayJs(b.date).valueOf() -
                              sDayJs(a.date).valueOf()
                          )
                          .map((e, i) => (
                            <li className='tl-item' key={i}>
                              <span className='tl-node' aria-hidden='true' />
                              <span className='tl-date'>
                                {sDayJs(e.date).format('MMM D, YYYY')}
                              </span>
                              <span className='tl-text'>
                                {describeChange(e, { product: false })}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  <div className='rdetail__block'>
                    <div className='dl'>Ingredients</div>
                    {item.ingredients.length > 0 ? (
                      <div className='ings'>
                        {item.ingredients.map((ing, i) => (
                          <span className='ing' key={i}>
                            {ing}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className='muted'>Not listed.</span>
                    )}
                  </div>
                </div>
              </details>
            )
          })}
        </div>
      </div>
    </section>
  )
}
